import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const profileOperations = {
    async load(userId) {
      if (!userId) return;
      setProfileLoading(true);
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
        
        if (error) {
          console.log('Profile load error:', error);
          
          // If profile doesn't exist (406 error), create it
          if (error.code === 'PGRST116' || error.message.includes('0 rows')) {
            console.log('📝 Profile not found, creating it now...');
            await this.create(userId);
            // Retry loading after creation
            const { data: newData, error: retryError } = await supabase
              ?.from('user_profiles')
              ?.select('*')
              ?.eq('id', userId)
              ?.single();
            
            if (!retryError && newData) {
              setUserProfile(newData);
              console.log('✅ Profile created and loaded successfully');
            }
          }
        } else {
          setUserProfile(data);
        }
      } catch (error) {
        console.log('Profile load exception:', error);
      } finally {
        setProfileLoading(false);
      }
    },
    
    // Create profile manually (fallback)
    async create(userId) {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError || !user) {
          console.error('❌ Cannot get user data:', userError);
          return;
        }

        // Enhanced name extraction for OAuth providers
        const fullName = 
          user.user_metadata?.full_name || 
          user.user_metadata?.name || 
          user.user_metadata?.display_name ||
          (user.user_metadata?.first_name && user.user_metadata?.last_name 
            ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
            : null) ||
          user.email?.split('@')[0] || 
          'User';

        const { data, error } = await supabase
          .from('user_profiles')
          .insert({
            id: userId,
            email: user.email,
            full_name: fullName,
            tier: user.user_metadata?.tier || 'free'
          })
          .select()
          .single();

        if (error) {
          console.error('❌ Profile creation error:', error);
        } else {
          console.log('✅ Profile created:', data);
          return data;
        }
      } catch (error) {
        console.error('❌ Profile creation exception:', error);
      }
    },
    
    clear() {
      setUserProfile(null);
      setProfileLoading(false);
    }
  };

  const authStateHandlers = {
    onChange: (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id);
      } else {
        profileOperations?.clear();
      }
    }
  };

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session);
    });

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    );

    return () => subscription?.unsubscribe();
  }, []);

  /**
   * Sign in with OAuth provider (Google, LinkedIn, etc.)
   */
  const signInWithOAuth = async (provider) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: provider.toLowerCase(), // 'google' or 'linkedin'
        options: {
          redirectTo: `${window.location.origin}/user-dashboard`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) {
        return { error: error.message };
      }

      // OAuth redirects to provider, so we don't return here
      // The redirect will bring user back to redirectTo URL
      return { data, error: null };
    } catch (error) {
      return { error: 'OAuth sign-in failed. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) {
        return { error: error?.message };
      }
      return { error: null };
    } catch (error) {
      return { error: 'Network error. Please check your connection.' };
    }
  };

  const refreshUserProfile = async () => {
    if (!user?.id) return;
    await profileOperations.load(user.id);
  }; 

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signOut,
    signInWithOAuth,
    refreshUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;